// @flow

import window from 'global/window';
import Bugsnag from 'app/lib/Bugsnag';
import moment from 'moment';
import 'moment-duration-format';

export type DateLike = moment$Moment | string | number | Date | Array<number>;

// Detect whether to use 12-hour time by querying the
// user's locale's time formatting preferences
const shouldUse12HourTime = (() => {
  if (window.Intl && window.Intl.DateTimeFormat) {
    try {
      const userLocaleFormatter = new window.Intl.DateTimeFormat(
        // For whatever reason, Chrome lies about the 'default' locale
        // (for instance, my computer, set to en-AU in Chrome's options,
        // will use en-GB, which has totally different time formatting),
        // so instead we feed in the user's specified language, if present.
        window.navigator.language || 'default',
        {
          // We need to specify this to have the `hour12` property appear
          hour: 'numeric'
        }
      );

      // Some browsers, like Android's, don't support the `resolvedOptions` method
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat/resolvedOptions#Browser_compatibility
      if (userLocaleFormatter.resolvedOptions) {
        return userLocaleFormatter.resolvedOptions().hour12;
      }
    } catch (err) {
      // Just in case, we're adding error catching here because not
      // doing so would mean a (completely) broken app, and I don’t
      // know what errors are possible here.
      //
      // It’s plausible that there are languages the browser can report
      // which aren’t supported by the Intl API, but none of the ones I
      // can come up with do.
      //
      // If errors come from here, we should see them,
      // and then correctly handle and address them!
      Bugsnag.notify(err);
    }
  }

  // Fall back to our original preferences for time formatting
  return true;
})();

// gets the clock time formatter
const getTimeFormatter = (withSeconds = false) => (
  `${shouldUse12HourTime ? 'h' : 'HH'}:mm${withSeconds ? ':ss' : ''}${shouldUse12HourTime ? ' A' : ''}`
);

// gets the calendar date formatter
const getDateFormatter = (withSeconds = false, withYear = true) => (
  `ddd Do MMM${withYear ? ' YY' : ''} [at] ${getTimeFormatter(withSeconds)}`
);

type RelativeDateOptions = {
  seconds?: boolean,
  inPast?: boolean,
  capitalized?: boolean
};

// gets a friendly, relative date string, optionally with seconds,
// and optionally with relative date names in lowercase
//
// For example:
//   "Today at 12:03 PM"
//   "yesterday at 11:01 AM"
//   "Wed 13 Nov at 1:00 AM"
//   "Fri 1 Jan 2012 at 4:02 PM"
export function getRelativeDateString(time: DateLike, options: RelativeDateOptions = {}) {
  // $FlowFixMe Not sure why these are failing — I think there is an issue with our libdef.
  const formats = Object.assign({}, moment.localeData()._calendar);
  const timeFormat = getTimeFormatter(options.seconds);

  if (!options.inPast) {
    formats.lastWeek = formats.lastWeek.replace('[Last] ', '');
    formats.nextWeek = `[Next] ${formats.lastWeek}`;
  }

  formats.sameElse = function(date) {
    return getDateFormatter(options.seconds, this.year() !== moment(date).year());
  };

  if (!options.capitalized) {
    Object.keys(formats).forEach((calendarString) => {
      if ((typeof formats[calendarString]) === 'string') {
        formats[calendarString] = formats[calendarString]
          .replace(/\[[^\]]+\]/g, (replacement) => replacement.toLowerCase())
          .replace('LT', timeFormat);
      }
    });
  } else {
    Object.keys(formats).forEach((calendarString) => {
      if ((typeof formats[calendarString]) === 'string') {
        formats[calendarString] = formats[calendarString]
          .replace('LT', timeFormat);
      }
    });
  }

  return moment(time).calendar(moment(), formats);
}

// gets an absolute date string, optionally with seconds
export function getDateString(date: DateLike, withSeconds: boolean = false, withYear: boolean = true) {
  return moment(date).format(getDateFormatter(withSeconds, withYear));
}

export function getDuration(from: ?DateLike, to: ?DateLike = moment()) {
  if (!to || !from) {
    return moment.duration(0);
  }

  return moment.duration(moment(to).diff(from));
}

const LONG_DURATION_STRING = 'w [weeks], d [days], h [hours], m [minutes], s [seconds]';
const SHORT_DURATION_STRING = 'w[w] d[d] h[h] m[m] s[s]';

const DURATION_FORMATS = {
  full: [LONG_DURATION_STRING, { largest: 3, trim: false }],
  medium: [LONG_DURATION_STRING, { largest: 1, trim: false }],
  short: [SHORT_DURATION_STRING, { largest: 2, trim: false }],
  micro: [SHORT_DURATION_STRING, { largest: 1, trim: false }]
};

export function getDurationString(seconds: ?number, format: $Keys<typeof DURATION_FORMATS> = 'full') {
  if (getDurationString.formats.indexOf(format) === -1) {
    throw new Error(`getDurationString: Unknown format \`${format}\`.`);
  }

  const [template, options] = DURATION_FORMATS[format];
  // $FlowFixMe Not sure why these are failing — I think there is an issue with our libdef.
  return moment.duration(seconds, 'seconds').format(template, options);
}

getDurationString.formats = Object.keys(DURATION_FORMATS);
